import React, { useState } from "react";
import { createPortal } from "react-dom";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";

function buildCsvContent(columns, rows) {
  const header = columns.join(";");
  const body = rows
    .map((row) =>
      columns
        .map((key) => {
          const raw = row[key] ?? "";
          const value = typeof raw === "string" ? raw : JSON.stringify(raw);
          const escaped = value.replace(/\"/g, '""');
          if (/[\";\n]/.test(escaped)) {
            return `"${escaped}"`;
          }
          return escaped;
        })
        .join(";")
    )
    .join("\n");
  return `${header}\n${body}`;
}

export function ModaArticoliExportContent({
  token,
  filters,
  sortBy,
  sortDirection,
  totalRows = 0,
  onClose,
  embedded = false,
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleExport = async () => {
    if (!token) {
      setError("Token non disponibile.");
      return;
    }
    setDownloading(true);
    setError("");
    setSuccess("");
    try {
      const response = await fetch(`${apiUrl}/moda/articoli/export`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          filters,
          sortBy,
          sortDirection,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error || "Impossibile esportare il CSV.");
      }
      const payload = await response.json();
      const rows = payload?.data ?? payload?.items ?? [];
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("Nessun dato da esportare.");
      }
      const columns = Object.keys(rows[0]);
      const content = buildCsvContent(columns, rows);
      const blob = new Blob([content], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `${today}-Export articoli.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess("Download avviato.");
      if (!embedded) {
        onClose?.();
      }
    } catch (err) {
      setError(err.message ?? "Errore imprevisto durante l'esportazione.");
    } finally {
      setDownloading(false);
    }
  };

  const { buttonClassesWhite, buttonClassesGreen } = stilePulsanti;
  const isOverLimit = totalRows > 10000;

  return (
    <div>
      <h3 className="mt0 mb3">Esporta CSV</h3>
      <p className="mb3">
        Verranno esportati tutti gli articoli corrispondenti ai filtri attuali.
      </p>
      {isOverLimit && (
        <div className="mb3 pa2 br2 bg-washed-red dark-red">
          Non è possibile esportare la selezione corrente, al momento sono
          supportati massimo 10000 articoli. Prova a restringere la ricerca.
        </div>
      )}
      {error && <div className="mb3 pa2 br2 bg-washed-red dark-red">{error}</div>}
      {success && (
        <div className="mb3 pa2 br2 bg-washed-green dark-green">{success}</div>
      )}
      <div className="mt-auto flex flex-wrap justify-between items-center">
        {!embedded && (
          <div className="mb2 mb0-ns">
            <button type="button" className={buttonClassesWhite} onClick={onClose}>
              Annulla
            </button>
          </div>
        )}
        {!isOverLimit && (
          <button
            type="button"
            className={`${buttonClassesGreen} mr2 exportButton`}
            disabled={downloading || isOverLimit}
            onClick={handleExport}
          >
            {downloading ? "Esporto…" : "Scarica CSV"}
          </button>
        )}
      </div>
    </div>
  );
}

function ModaArticoliExportModal({
  open,
  onClose,
  token,
  filters,
  sortBy,
  sortDirection,
  totalRows = 0,
  temaColori = "rootTema",
}) {
  if (!open || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className={`modal ${temaColori}`} onClick={onClose}>
      <div
        className="w-90 w-50-l center pa4 br3 shadow-4 sfondoBiancoTema relative flex flex-column"
        style={{ maxHeight: "80vh" }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="bn bg-transparent absolute top-1 right-1 pointer f3"
          onClick={onClose}
        >
          ×
        </button>
        <ModaArticoliExportContent
          token={token}
          filters={filters}
          sortBy={sortBy}
          sortDirection={sortDirection}
          totalRows={totalRows}
          onClose={onClose}
        />
      </div>
    </div>,
    document.body
  );
}

export default ModaArticoliExportModal;
